// RegisterComponent.js
import React, { useEffect, useRef, useState } from "react";
import { useAtom } from "jotai";
import { emailAtom, registeredUserIdAtom } from "@/store/atoms";

import {
  useI18N,
  useRegister,
  useZoomDetection,
  useModalHeight,
} from "common/hooks";
import { isPasswordOneNumberSymbolValid, isPasswordValid } from "common/utils";
import Loader from "@/components/common/Loader";
import { useDarkMode } from "@/hooks/useDarkMode";
import { createPageAnalytics } from "common/analytics";
import { applyUserIdentity, signupDateToday } from "@/analytics/identity";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import Checkmark from "@/components/common/Checkmark";
import { publicPrivacyUrl, publicTermsUrl } from "common/config";
import FloatingLabelInput from "@/components/InputBoxes/TextInput";
import Checkbox from "@/components/common/Checkbox";
import WideButton from "@/components/common/WideButton";
import { ArrowLeft } from "lucide-react";
import { StandardButton } from "@/components/common/StandardButton";

interface RegisterProps {
  onClose: () => void;
  title: string;
}

const RegisterComponent = ({ onClose, title }: RegisterProps) => {
  const isZoomedIn = useZoomDetection();
  const i18n = useI18N();
  const isDark = useDarkMode();
  const [email] = useAtom(emailAtom);
  const { setScreenIndex } = useAuthModals();
  const signupAnalytics = createPageAnalytics("auth_signup");
  const [, setRegisteredUserId] = useAtom(registeredUserIdAtom);
  const { modalStyle, modalClassName } = useModalHeight();
  const [termsAgreed, setTermsAgreed] = useState(false);

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");

  const { error, handleRegister, isLoading, result } = useRegister();

  useEffect(() => {
    signupAnalytics.track("page_view_page", {
      step_index: 1,
      step_name: "credentials",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function registrationValild() {
    const isValid = isPasswordValid(password);
    return isValid && email.length > 0 && name.length > 0;
  }

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const onHandleRegister = async () => {
    handleRegister({
      name,
      email,
      password,
      password_confirmation: password,
      t_and_c: "true",
      gdpr: "true",
      socialUser_authToken: "",
      socialUser_provider: "",
      brand_invitation_code: "",
    });
  };

  useEffect(() => {
    if (error) {
      console.log("500 Error", error);
    }
  }, [error]);

  useEffect(() => {
    if (result) {
      setRegisteredUserId(result.userId);
      console.log("Register done result", result);

      // Outcome: account actually created (email path).
      signupAnalytics.track("continue_action_button", {
        account_creation_method: "email",
      });

      // New centralized identity (additive). New email signup -> not yet verified.
      applyUserIdentity(
        { id: result.userId, email, name },
        {
          account_creation_method: "email",
          email_verified: false,
          account_creation_date: signupDateToday(),
        },
      );

      setScreenIndex(AuthModalScreen.OnboardingStep1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, setScreenIndex, onClose, setRegisteredUserId, email, name]);

  const onBack = async () => {
    setName("");
    setPassword("");
    setScreenIndex(AuthModalScreen.EnterEmail);
  };

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  return (
    <section
      style={modalStyle}
      className="relative bg-bg-brand-secondary-default rounded-xl h-full flex flex-col overflow-y-auto"
      role="none"
      aria-labelledby="modal-title"
    >
      <header
        className={`py-4 min-h-14 flex items-center justify-center text-center px-6 border-b border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn ? "sticky top-0 z-10" : "relative z-10"} relative`}
      >
        <StandardButton
          onClick={onBack}
          variant="back"
          icon={<ArrowLeft color={isDark ? "#A39FE8" : "#352FC3"} />}
          aria-label="Go back"
          tabIndex={0}
        />
        <h1
          className="title-s-semibold text-center text-fg-neutral-strong"
          ref={firstFocusRef}
          tabIndex={0}
          id="modal-title"
        >
          {title}
        </h1>
      </header>

      <div
        aria-label="Modal Content"
        className="py-3 px-6 md:py-6 md:px-14 flex-1 flex flex-col"
        role="none"
      >
        <div className="flex-1 flex flex-col">
          {/* content comes below this */}
          <h2 className="text-fg-neutral-strong my-[16px]">
            Thanks for your email, please provide more information for:
          </h2>
          <div className="my-0 md:my-2 !text-fg-brand-subtle">{email}</div>
          <div className="pb-5">
            <hr />
          </div>
          <div className="flex flex-col gap-4 mb-6">
            <FloatingLabelInput
              label="Name"
              required
              type="text"
              value={name}
              placeholder="Name"
              onChange={(event) => {
                setName(event.target.value);
              }}
              onClear={() => {
                setName("");
              }}
            />
            <FloatingLabelInput
              label="Password"
              type="Password"
              placeholder="Password"
              required
              value={password}
              onChange={handlePasswordChange}
              errorMessage={error?.message}
              onToggleVisibility={(visible) =>
                signupAnalytics.track("password_visibility_toggle_toggle", {
                  visibility_state: visible ? "shown" : "hidden",
                })
              }
            />
          </div>

          {isLoading && <Loader />}

          <div className="mb-6 text-fg-neutral-strong body-l-semibold">
            <span>{i18n.get("yourPwdShouldContain")}</span>

            <div className="flex items-center mb-3 mt-3">
              <Checkmark disabled={password.length < 6} />
              <span className="ml-2 label-m-medium text-fg-neutral-strong">
                {i18n.get("passwordAtLeast6Chars")}
              </span>
            </div>

            <div className="flex items-center">
              <Checkmark disabled={!isPasswordOneNumberSymbolValid(password)} />
              <span className="ml-2 label-m-medium text-fg-neutral-strong">
                {i18n.get("passwordOneSymbolOrNumber")}
              </span>
            </div>
          </div>

          <div className="flex flex-row items-start">
            <div className="mt-1 mr-2">
              <Checkbox
                label=""
                checked={termsAgreed}
                onCheck={(value) => {
                  setTermsAgreed(value);
                }}
              />
            </div>
            <div className="">
              <span className="text-fg-neutral-strong label-m-medium mr-1">
                {i18n.get("bySelectingAgreeAndContinue")}
              </span>
              <a
                href={publicTermsUrl}
                target="_blank"
                className=" label-m-medium leading-normal mr-1 underline"
                onClick={() =>
                  signupAnalytics.track(
                    "terms_and_conditions_link_link_button",
                    {
                      link_destination: "terms_and_conditions",
                    },
                  )
                }
              >
                {i18n.get("termsAndConditions")}
              </a>
              <span className=" label-m-medium leading-normal mr-1">
                {i18n.get("and")}
              </span>
              <a
                href={publicPrivacyUrl}
                target="_blank"
                className=" label-m-medium leading-normal underline"
                onClick={() =>
                  signupAnalytics.track("privacy_notice_link_link_button", {
                    link_destination: "privacy_notice",
                  })
                }
              >
                {i18n.get("privacyNotice")}
              </a>
            </div>
          </div>
          <div className="flex-1" />
          <div className="flex justify-center mb-4">
            <WideButton
              disabled={!registrationValild() || !termsAgreed}
              onClick={() => onHandleRegister()}
            >
              {i18n.get("continue")}
            </WideButton>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RegisterComponent;
